import React from "react"
import { Col, Flex, Row, Tag, Typography } from "antd"
import { ClockCircleOutlined, CalendarOutlined, GlobalOutlined } from "@ant-design/icons"
import { useSearchParams } from "react-router-dom"
import { useWizardContext } from "../../../WizardContext"
import styles from "./PreviewScheduling.module.css"

const { Title, Text } = Typography

const DAY_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]
const DAY_SHORT = {
  monday: "Mon", tuesday: "Tue", wednesday: "Wed",
  thursday: "Thu", friday: "Fri", saturday: "Sat", sunday: "Sun"
}

const getActiveDays = (available_days) => {
  if (!available_days) return []
  if (Array.isArray(available_days)) return available_days
  return DAY_ORDER.filter((d) => available_days[d])
}

const formatHour = (h) => h?.format?.("h:mm A") ?? h ?? ""

export const PreviewScheduling = () => {
  const { form } = useWizardContext()
  const [searchParams] = useSearchParams()
  const isTicketed = searchParams.get("type") === "ticketed"
  const available_days = form.getFieldValue("available_days")
  const available_hours = form.getFieldValue("available_hours") || []
  const time_zone = form.getFieldValue("time_zone")
  const isSeasonalExperience = form.getFieldValue("isSeasonalExperience")
  const timeBlocks = form.getFieldValue("timeBlocks") || []

  const activeDays = getActiveDays(available_days)
  const activeHours = available_hours.filter((h) => h?.start || h?.end)

  const seasonalRanges = timeBlocks
    .map((idx) => {
      const start = form.getFieldValue(`seasonStart_${idx}`)
      const end = form.getFieldValue(`seasonEnd_${idx}`)
      if (!start || !end) return null
      return `${start.format("MMM D")} – ${end.format("MMM D")}`
    })
    .filter(Boolean)

  const hasData = activeDays.length > 0 || activeHours.length > 0 || time_zone

  if (!hasData) return null

  return (
    <div className={styles.container}>
      <Title level={3} className={styles.title}>{"Availability"}</Title>

      {(time_zone || (!isTicketed && isSeasonalExperience != null)) && (
        <Row gutter={[12, 12]} className={styles.metaRow}>
          {time_zone && (
            <Col xs={24} sm={12}>
              <div className={styles.metaCard}>
                <Flex align="center" gap="small" className={styles.metaCardHeader}>
                  <span className={styles.metaIconWrap}>
                    <GlobalOutlined className={styles.metaIcon} />
                  </span>
                  <Text className={styles.metaLabel}>{"Timezone"}</Text>
                </Flex>
                <Text strong className={styles.metaValue}>{time_zone}</Text>
              </div>
            </Col>
          )}
          {!isTicketed && (
            <Col xs={24} sm={12}>
              <div className={styles.metaCard}>
                <Flex align="center" gap="small" className={styles.metaCardHeader}>
                  <span className={styles.metaIconWrap}>
                    <CalendarOutlined className={styles.metaIcon} />
                  </span>
                  <Text className={styles.metaLabel}>{"Schedule Type"}</Text>
                </Flex>
                {isSeasonalExperience ? (
                  <Tag color="orange" className={styles.scheduleTag}>{"Seasonal"}</Tag>
                ) : (
                  <Tag color="green" className={styles.scheduleTag}>{"Year-round"}</Tag>
                )}
              </div>
            </Col>
          )}
        </Row>
      )}

      {!isTicketed && isSeasonalExperience && seasonalRanges.length > 0 && (
        <div className={styles.section}>
          <Flex align="center" gap="small" className={styles.sectionHeader}>
            <CalendarOutlined className={styles.sectionIcon} />
            <Text strong className={styles.sectionTitle}>{"Season Dates"}</Text>
          </Flex>
          <Flex wrap="wrap" gap="small">
            {seasonalRanges.map((range) => (
              <Tag key={range} color="orange" className={styles.rangeTag}>{range}</Tag>
            ))}
          </Flex>
        </div>
      )}

      {activeDays.length > 0 && (
        <div className={styles.section}>
          <Flex align="center" gap="small" className={styles.sectionHeader}>
            <CalendarOutlined className={styles.sectionIcon} />
            <Text strong className={styles.sectionTitle}>{"Available Days"}</Text>
          </Flex>
          <Flex gap="small" className={styles.dayGrid}>
            {DAY_ORDER.map((d) => {
              const isActive = activeDays.includes(d)
              return (
                <span key={d} className={isActive ? styles.dayActive : styles.dayInactive}>
                  {DAY_SHORT[d]}
                </span>
              )
            })}
          </Flex>
        </div>
      )}

      {activeHours.length > 0 && (
        <div className={styles.section}>
          <Flex align="center" gap="small" className={styles.sectionHeader}>
            <ClockCircleOutlined className={styles.sectionIcon} />
            <Text strong className={styles.sectionTitle}>{"Time Slots"}</Text>
          </Flex>
          <Flex wrap="wrap" gap="small">
            {activeHours.map((h, i) => (
              <span key={i} className={styles.timeSlot}>
                {`${formatHour(h.start)} – ${formatHour(h.end)}`}
              </span>
            ))}
          </Flex>
        </div>
      )}
    </div>
  )
}
